import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { gdprService } from '../../services/api';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui';
import DeleteAccountModal from './DeleteAccountModal';
import PrivacyPolicyContent from './PrivacyPolicyContent';
import { Shield, Download, Trash2, FileText } from 'lucide-react';

/**
 * Card per la gestione privacy e dati personali
 * Include: export dati e eliminazione account
 */
function GdprSettingsCard() {
  const { deleteAccount } = useAuth();
  const { toast } = useToast();

  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const response = await gdprService.exportData();

      // Crea e scarica file JSON
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `i-miei-dati-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Export completato', 'I tuoi dati sono stati scaricati');
    } catch (error) {
      toast.error(
        'Errore',
        error.response?.data?.message || "Errore durante l'export"
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async (password) => {
    setIsDeleting(true);
    try {
      await deleteAccount(password);
      toast.success(
        'Account eliminato',
        'Il tuo account è stato eliminato con successo'
      );
      // Il redirect al login viene gestito automaticamente dall'AuthContext
    } catch (error) {
      toast.error(
        'Errore',
        error.response?.data?.message || "Errore durante l'eliminazione"
      );
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Privacy e Dati Personali
          </CardTitle>
          <CardDescription>
            Gestisci i tuoi dati secondo le normative GDPR
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Visualizza Privacy Policy */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-4">
            <div className="space-y-1">
              <p className="font-medium">Informativa sulla Privacy</p>
              <p className="text-sm text-muted-foreground">
                Consulta l'informativa completa sul trattamento dei tuoi dati
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowPrivacyModal(true)}
              className="w-full sm:w-auto"
            >
              <FileText className="mr-2 h-4 w-4" />
              Visualizza
            </Button>
          </div>

          {/* Export dati */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-4">
            <div className="space-y-1">
              <p className="font-medium">Scarica i tuoi dati</p>
              <p className="text-sm text-muted-foreground">
                Ottieni una copia di tutti i dati associati al tuo account
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleExportData}
              isLoading={isExporting}
              className="w-full sm:w-auto"
            >
              <Download className="mr-2 h-4 w-4" />
              Scarica
            </Button>
          </div>

          {/* Elimina account */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-destructive/20 rounded-lg bg-destructive/5 gap-4">
            <div className="space-y-1">
              <p className="font-medium text-destructive">Elimina account</p>
              <p className="text-sm text-muted-foreground">
                Elimina permanentemente il tuo account e tutti i dati associati
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteModal(true)}
              className="w-full sm:w-auto"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Elimina
            </Button>
          </div>
        </CardContent>
      </Card>

      <DeleteAccountModal
        open={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        onConfirm={handleDeleteAccount}
        isLoading={isDeleting}
      />

      <Dialog open={showPrivacyModal} onOpenChange={setShowPrivacyModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
              <DialogTitle className="text-base sm:text-lg">Informativa sulla Privacy</DialogTitle>
            </div>
            <DialogDescription className="text-sm">
              Informativa sul trattamento dei dati personali ai sensi del GDPR.
            </DialogDescription>
          </DialogHeader>
          <div 
            className="max-h-64 sm:max-h-80 overflow-y-auto border rounded-md p-3 sm:p-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <PrivacyPolicyContent />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default GdprSettingsCard;
